import * as React from "react";
import { polyfill } from "react-lifecycles-compat";
import ValidatorForm, { FormContext, FormContextValue } from "./ValidatorForm";
import { debounce } from "../utils/utils";
import {
  ValidatorComponentProps,
  ValidatorComponentState,
} from "../interface/Validator";

type DebouncedValidate = {
  (value: any, includeRequired?: boolean, dryRun?: boolean): void;
  cancel?: () => void;
};

class ValidatorComponent<
  P extends ValidatorComponentProps = ValidatorComponentProps
> extends React.Component<P, ValidatorComponentState> {
  static contextType = FormContext;

  declare context: React.ContextType<typeof FormContext>;

  validateDebounced: DebouncedValidate | null = null;

  form: NonNullable<FormContextValue["form"]> | null = null;

  debounceTime = 0;

  instantValidate = true;

  invalid: number[] = [];

  state: ValidatorComponentState = {
    isValid: true,
    value: this.props.value,
    errorMessages: this.props.errorMessages,
    validators: this.props.validators,
  };

  renderValidatorComponent(): React.ReactNode {
    throw new Error("Method not implemented.");
  }

  componentDidMount() {
    this.configure();
  }

  componentDidUpdate(prevProps: P) {
    const validatorsChanged = prevProps.validators !== this.props.validators;
    const errorMessagesChanged =
      prevProps.errorMessages !== this.props.errorMessages;
    const valueChanged = prevProps.value !== this.props.value;

    if (validatorsChanged || errorMessagesChanged || valueChanged) {
      this.setState({
        value: this.props.value,
        validators: this.props.validators,
        errorMessages: this.props.errorMessages,
      });
    }

    if (this.instantValidate && valueChanged && this.validateDebounced) {
      this.validateDebounced(
        this.props.value,
        this.props.withRequiredValidator
      );
    }
  }

  componentWillUnmount() {
    this.form?.detachFromForm?.(this);

    if (this.validateDebounced?.cancel) {
      this.validateDebounced.cancel();
    }
  }

  shouldComponentUpdate(nextProps: P, nextState: ValidatorComponentState) {
    return this.props !== nextProps || this.state !== nextState;
  }

  configure = () => {
    const form = this.context?.form;

    if (!form?.attachToForm || !form?.detachFromForm) {
      console.error(
        "MuiTextField debe estar dentro de tu <MuiForm> local. No mezcles este MuiTextField con MuiForm de react-mui-form-validator."
      );
      return;
    }

    this.form = form;
    this.form.attachToForm(this);

    this.instantValidate = this.form.instantValidate;
    this.debounceTime = this.form.debounceTime;

    this.validateDebounced = debounce(
      (value: any, includeRequired?: boolean, dryRun?: boolean) => {
        void this.validate(value, includeRequired, dryRun);
      },
      this.debounceTime
    );
  };

  getErrorMessage = (): string | boolean => {
    const { errorMessages } = this.state;

    if (!errorMessages) {
      return true;
    }

    if (typeof errorMessages === "string") {
      return errorMessages;
    }

    if (Array.isArray(errorMessages) && this.invalid.length > 0) {
      return errorMessages[this.invalid[0]] ?? true;
    }

    return true;
  };

  validate = async (
    value: any,
    includeRequired = false,
    dryRun = false
  ): Promise<boolean> => {
    const validators = this.state.validators;

    if (!validators || !Array.isArray(validators)) {
      return true;
    }

    const results = await Promise.all(
      validators.map((validator) =>
        ValidatorForm.getValidator(validator, value, includeRequired)
      )
    );

    this.invalid = [];

    let valid = true;

    results.forEach((result, key) => {
      if (!result) {
        valid = false;
        this.invalid.push(key);
      }
    });

    if (!dryRun) {
      this.setState({ isValid: valid }, () => {
        this.props.validatorListener?.(this.isValid());
      });
    }

    return valid;
  };

  isValid = (): boolean => {
    return this.state.isValid === undefined ? true : this.state.isValid;
  };

  makeInvalid = () => {
    this.setState({ isValid: false });
  };

  makeValid = () => {
    this.setState({ isValid: true });
  };

  renderComponent = (): React.ReactNode => {
    return this.renderValidatorComponent();
  };

  render() {
    const { containerProps } = this.props;

    return <div {...containerProps}>{this.renderComponent()}</div>;
  }
}

polyfill(ValidatorComponent);

export default ValidatorComponent;